import type {
  BuilderSelections,
  ConsoleRequest,
  DataRequest,
  EndpointId,
  HttpMethod,
  OutputFormat,
  TableMetadata,
} from "./types";

// Display/copy only — the console always calls the API through its own /backend proxy.
const PUBLIC_API_BASE_URL = (
  import.meta.env.VITE_API_PUBLIC_URL ?? "https://api.jobindsats.dk"
).replace(/\/+$/, "");

export function buildConsoleRequest({
  endpoint,
  method,
  format,
  subjectId,
  tableId,
  data,
}: {
  endpoint: EndpointId;
  method: HttpMethod;
  format: OutputFormat;
  subjectId: string;
  tableId: string;
  data?: DataRequest;
}): ConsoleRequest {
  let path: string;
  let body: Record<string, unknown> | DataRequest;

  switch (endpoint) {
    case "subjects":
      path = "/v3/subjects";
      body = { format };
      break;
    case "tables":
      path = "/v3/tables";
      body = subjectId ? { format, subject_id: subjectId } : { format };
      break;
    case "table":
      path = `/v3/table/${encodeURIComponent(tableId)}`;
      body = { format };
      break;
    case "data":
      path = `/v3/data/${encodeURIComponent(tableId)}`;
      body = data ?? { mgroup: {}, period: {}, hierarchy: {}, format };
      break;
  }

  if (method === "GET") {
    const query = new URLSearchParams();
    if (endpoint === "data") {
      addSelectors(query, "mgroup", body.mgroup);
      addSelectors(query, "period", body.period);
      addSelectors(query, "hierarchy", body.hierarchy);
    } else if (endpoint === "tables" && subjectId) {
      query.set("subject_id", subjectId);
    }
    query.set("format", format);
    path += `?${readableQuery(query)}`;
  }

  return {
    apiPath: path,
    apiUrl: `${PUBLIC_API_BASE_URL}${path}`,
    backendPath: path.replace(/^\/v3/, "/backend"),
    method,
    ...(method === "POST" ? { body } : {}),
  };
}

// "Valider og estimer": same body as the data request, POSTed to the /plan route.
export function buildPlanRequest(tableId: string, data: DataRequest): ConsoleRequest {
  const path = `/v3/data/${encodeURIComponent(tableId)}/plan`;
  return {
    apiPath: path,
    apiUrl: `${PUBLIC_API_BASE_URL}${path}`,
    backendPath: path.replace(/^\/v3/, "/backend"),
    method: "POST",
    body: data,
  };
}

export function initialSelections(metadata: TableMetadata): BuilderSelections {
  const hierarchyByDimension: Record<string, string> = {};

  // The first hierarchy the API lists is shown by default; the user can switch.
  for (const dimension of metadata.dimensions) {
    const hierarchy = dimension.hierarchies[0];
    if (!hierarchy) continue;
    hierarchyByDimension[dimension.dimension_id] = hierarchy.hierarchy_id;
  }

  return {
    measures: {},
    periods: {},
    hierarchyByDimension,
    hierarchyValues: {},
  };
}

export function buildDataRequest(
  metadata: TableMetadata,
  selections: BuilderSelections,
  format: OutputFormat,
): DataRequest {
  const mgroup: Record<string, string[]> = {};
  for (const group of metadata.mgroups) {
    const selected = selections.measures[group.mgroup_id] ?? [];
    if (selected.length === 0) continue;
    mgroup[group.mgroup_id] =
      selected.length === group.measures.length ? ["*"] : [...selected];
  }
  // The API accepts mgroup {"*": ["*"]} as shorthand for every group and measure.
  const allGroupsComplete =
    Object.keys(mgroup).length > 0 &&
    metadata.mgroups.every(
      (group) =>
        (selections.measures[group.mgroup_id] ?? []).length === group.measures.length,
    );

  const period = entriesWithValues(selections.periods);

  // A hierarchy value of "*" is API shorthand for every value in that hierarchy.
  const hierarchy: Record<string, string[]> = {};
  for (const dimension of metadata.dimensions) {
    const hierarchyId = selections.hierarchyByDimension[dimension.dimension_id];
    if (!hierarchyId) continue;
    const selected = selections.hierarchyValues[hierarchyId] ?? [];
    if (selected.length === 0) continue;

    const chosen = dimension.hierarchies.find((item) => item.hierarchy_id === hierarchyId);
    const valueIds = new Set(
      chosen?.levels.flatMap((level) =>
        level.values
          .map((value) => value.value_id)
          .filter((id): id is string => id !== null),
      ) ?? [],
    );
    const selectedIds = new Set(selected);
    const complete = valueIds.size > 0 && [...valueIds].every((id) => selectedIds.has(id));
    hierarchy[hierarchyId] = complete ? ["*"] : [...selected];
  }

  return { mgroup: allGroupsComplete ? { "*": ["*"] } : mgroup, period, hierarchy, format };
}

function entriesWithValues(values: Record<string, string[]>): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(values)
      .filter(([, selected]) => selected.length > 0)
      .map(([key, selected]) => [key, [...selected]]),
  );
}

// ":", "," and "/" are valid unencoded in a query string (RFC 3986) and are part
// of the API's selector syntax — keep them readable in the preview and copies.
function readableQuery(query: URLSearchParams) {
  return query
    .toString()
    .replace(/%3A/gi, ":")
    .replace(/%2C/gi, ",")
    .replace(/%2F/gi, "/");
}

function addSelectors(
  query: URLSearchParams,
  prefix: string,
  value: unknown,
) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return;
  for (const [key, selected] of Object.entries(value)) {
    if (Array.isArray(selected) && selected.length > 0) {
      query.set(`${prefix}.${key}`, selected.join(","));
    }
  }
}
